import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' },
];

export default function PrizeAnalytics() {
  useAuthGuard();

  const [period, setPeriod] = useState('30d');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/analytics/prizes`);
        url.searchParams.set('period', period);
        url.searchParams.set('limit', '50');
        if (category) url.searchParams.set('category', category);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRows(Array.isArray(data.data) ? data.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [period, category]);

  const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">Prize Analytics</h2>
        <p className="page-subtitle">Prize popularity ranking based on scan data</p>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="text"
              className="form-control"
              placeholder="Category filter"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ maxWidth: '180px', fontSize: '0.8125rem', height: '2.25rem' }}
            />
            {category && (
              <button className="btn btn-phoenix-secondary btn-sm" onClick={() => setCategory('')}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Prize Name</th>
                <th>Category</th>
                <th>Machine Type</th>
                <th className="text-end">Scans</th>
                <th className="text-end">Posts</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-4">Loading...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={6} className="text-danger py-4">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4" style={{ color: '#8a94ad' }}>No data</td></tr>
              )}
              {!loading && !error && rows.map((row, i) => (
                <tr key={i}>
                  <td className="text-muted">{i + 1}</td>
                  <td className="fw-medium">{row.prizeName || '-'}</td>
                  <td className="small">{row.category || '-'}</td>
                  <td><span className="badge bg-secondary">{row.machineType || '-'}</span></td>
                  <td className="text-end">{fmt(row.scanCount)}</td>
                  <td className="text-end">{fmt(row.postCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
